/**
 * Step 1: Community Contact Information
 * Collects basic contact details and community information for onboarding
 */

import React from 'react';
import Select, { StylesConfig } from 'react-select';
import { User, Building, Mail, Phone, Home, Users } from 'lucide-react';
import { StepComponentProps } from '../../../../../types/onboarding';
import { InfoTooltip } from '../../../../shared/tooltip/InfoTooltip';
import { getTooltipContent } from '../../../../../constants/onboarding';
import { Country, State, City } from 'country-state-city';

type SelectOption = { value: string; label: string };

export const Step1ContactInfo: React.FC<StepComponentProps> = ({
  formData,
  errors,
  onFieldChange,
  progressiveDisclosure,
}) => {
  const [useManualCityEntry, setUseManualCityEntry] = React.useState(false);

  const handleGovernanceChange = (governance: string, checked: boolean) => {
    const currentGovernance = (formData.governance as string[]) || [];
    if (checked) {
      onFieldChange('governance', [...currentGovernance, governance]);
    } else {
      onFieldChange(
        'governance',
        currentGovernance.filter((g) => g !== governance),
      );
    }
  };

  // Get US states for dropdown
  const usStates = State.getStatesOfCountry('US').map((state) => ({
    value: state.isoCode,
    label: state.name,
  }));

  // Get cities based on selected state
  const getCitiesForState = (stateCode: string) => {
    if (!stateCode) return [];
    return City.getCitiesOfState('US', stateCode).map((city) => ({
      value: city.name,
      label: city.name,
    }));
  };

  const cityOptions = formData.communityState ? getCitiesForState(formData.communityState) : [];

  // Custom styles for React Select to match our form styling
  const selectStyles: StylesConfig = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? '#3b82f6' : errors.communityCity ? '#ef4444' : '#cbd5e1',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.2)' : undefined,
      '&:hover': {
        borderColor: state.isFocused ? '#3b82f6' : '#94a3b8',
      },
      minHeight: '42px',
      fontSize: '14px',
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: '14px',
      backgroundColor: state.isFocused ? '#f1f5f9' : 'white',
      color: '#1e293b',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#64748b',
      fontSize: '14px',
    }),
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Building className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-3xl font-bold text-slate-900 mb-2">Tell Us About Your Community</h2>
        <p className="text-lg text-slate-600">
          Help us understand your community's basic structure so we can tailor our discussion to
          your specific needs.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Community Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Home className="w-5 h-5" />
            Community Information
          </h3>

          <div>
            <label
              htmlFor="communityName"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              Community Name *
            </label>
            <input
              type="text"
              id="communityName"
              value={formData.communityName}
              onChange={(e) => onFieldChange('communityName', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.communityName ? 'border-red-500' : 'border-slate-300'
              }`}
              placeholder="e.g., Sunset Ridge HOA"
            />
            {errors.communityName && (
              <p className="mt-1 text-sm text-red-600">{errors.communityName}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="communityLocation"
              className="block text-sm font-medium text-slate-700 mb-3"
            >
              Community Location *
            </label>

            {/* State Dropdown */}
            <div className="mb-3">
              <label
                htmlFor="communityState"
                className="block text-xs font-medium text-slate-600 mb-1"
              >
                State *
              </label>
              <Select
                id="communityState"
                value={usStates.find((state) => state.value === formData.communityState) || null}
                onChange={(selectedOption: SelectOption | null) => {
                  const stateCode = selectedOption?.value || '';
                  onFieldChange('communityState', stateCode);
                  // Reset city when state changes
                  if (stateCode !== formData.communityState) {
                    onFieldChange('communityCity', '');
                    setUseManualCityEntry(false); // Reset to dropdown mode
                  }
                }}
                options={usStates}
                styles={selectStyles}
                placeholder="Select state..."
                isSearchable={true}
                isClearable={true}
                className="react-select-container"
                classNamePrefix="react-select"
              />
              {errors.communityState && (
                <p className="mt-1 text-sm text-red-600">{errors.communityState}</p>
              )}
            </div>

            {/* City Autocomplete or Manual Entry */}
            <div>
              <label
                htmlFor="communityCity"
                className="block text-xs font-medium text-slate-600 mb-1"
              >
                City *
              </label>

              {!useManualCityEntry ? (
                <>
                  <Select
                    id="communityCity"
                    value={
                      cityOptions.find((city) => city.value === formData.communityCity) || null
                    }
                    onChange={(selectedOption: SelectOption | null) => {
                      onFieldChange('communityCity', selectedOption?.value || '');
                    }}
                    options={cityOptions}
                    styles={selectStyles}
                    placeholder={
                      formData.communityState ? 'Select city...' : 'Select state first...'
                    }
                    isSearchable={true}
                    isClearable={true}
                    isDisabled={!formData.communityState}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    noOptionsMessage={() =>
                      formData.communityState ? 'No cities found' : 'Select state first'
                    }
                  />
                  {formData.communityState && (
                    <button
                      type="button"
                      onClick={() => {
                        setUseManualCityEntry(true);
                        onFieldChange('communityCity', '');
                      }}
                      className="mt-1 text-xs text-blue-600 hover:text-blue-700 underline"
                    >
                      City not in list? Enter manually
                    </button>
                  )}
                </>
              ) : (
                <>
                  <input
                    type="text"
                    id="communityCityManual"
                    value={formData.communityCity}
                    onChange={(e) => onFieldChange('communityCity', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.communityCity ? 'border-red-500' : 'border-slate-300'
                    }`}
                    placeholder="Enter city name..."
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setUseManualCityEntry(false);
                      onFieldChange('communityCity', '');
                    }}
                    className="mt-1 text-xs text-blue-600 hover:text-blue-700 underline"
                  >
                    Back to city selector
                  </button>
                </>
              )}

              {errors.communityCity && (
                <p className="mt-1 text-sm text-red-600">{errors.communityCity}</p>
              )}
              {(errors.communityLocation || errors.communityState) && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.communityLocation || errors.communityState}
                </p>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="totalUnits"
              className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1"
            >
              Number of Voting Parcels *
              <InfoTooltip {...getTooltipContent('voting-parcels')} iconSize={14} />
            </label>
            <input
              type="text"
              id="totalUnits"
              value={formData.totalUnits ? Number(formData.totalUnits).toLocaleString() : ''}
              onChange={(e) => {
                // Remove commas and non-numeric characters except for the value itself
                const numericValue = e.target.value.replace(/[^\d]/g, '');
                // Enforce min/max constraints
                const num = parseInt(numericValue);
                if (numericValue === '' || (num >= 1 && num <= 10000)) {
                  onFieldChange('totalUnits', numericValue);
                }
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center font-semibold ${
                errors.totalUnits ? 'border-red-500' : 'border-slate-300'
              }`}
              placeholder="e.g., 406"
            />
            <p className="text-xs text-slate-500 mt-1">
              Enter the exact number of voting parcels/units in your community
            </p>
            {errors.totalUnits && <p className="mt-1 text-sm text-red-600">{errors.totalUnits}</p>}
          </div>

          <div>
            <label
              htmlFor="yearEstablished"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              Year Community Established *
            </label>
            <select
              id="yearEstablished"
              value={(formData.yearEstablished as number) || ''}
              onChange={(e) => onFieldChange('yearEstablished', parseInt(e.target.value) || null)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${
                errors.yearEstablished ? 'border-red-500' : 'border-slate-300'
              }`}
            >
              <option value="">Select year established...</option>
              <option value="0">I don't know / Not sure</option>
              <optgroup label="Recent Years">
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </optgroup>
              <optgroup label="By Decade (if exact year unknown)">
                <option value="2020">2020s (2020-2029)</option>
                <option value="2010">2010s (2010-2019)</option>
                <option value="2000">2000s (2000-2009)</option>
                <option value="1990">1990s (1990-1999)</option>
                <option value="1980">1980s (1980-1989)</option>
                <option value="1970">1970s (1970-1979)</option>
                <option value="1960">1960s (1960-1969)</option>
                <option value="1950">1950s (1950-1959)</option>
                <option value="1940">1940s (1940-1949)</option>
                <option value="1930">1930s (1930-1939)</option>
                <option value="1920">1920s (1920-1929)</option>
                <option value="1910">1910s (1910-1919)</option>
                <option value="1900">1900s (1900-1909)</option>
                <option value="1890">1890s (1890-1899)</option>
                <option value="1880">1880s (1880-1889)</option>
                <option value="1870">1870s (1870-1879)</option>
                <option value="1860">1860s (1860-1869)</option>
                <option value="1850">1850s (1850-1859)</option>
                <option value="1840">1840s (1840-1849)</option>
                <option value="1830">1830s (1830-1839)</option>
                <option value="1820">1820s (1820-1829)</option>
                <option value="1810">1810s (1810-1819)</option>
                <option value="1800">1800s (1800-1809)</option>
              </optgroup>
              <optgroup label="Specific Year">
                {Array.from(
                  { length: new Date().getFullYear() - 1800 + 1 },
                  (_, i) => new Date().getFullYear() - i,
                ).map((year) => (
                  <option key={`specific-${year}`} value={year}>
                    {year}
                  </option>
                ))}
              </optgroup>
            </select>
            <p className="text-xs text-slate-500 mt-1">
              Select a decade range if you're not sure of the exact year
            </p>
            {errors.yearEstablished && (
              <p className="mt-1 text-sm text-red-600">{errors.yearEstablished}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="communityType"
              className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1"
            >
              Community Type *
              <InfoTooltip
                title="Community Types Explained"
                content="Different community types have different management needs. Hover over each option below to learn more about what each type means."
                iconSize={14}
              />
            </label>
            <select
              id="communityType"
              value={formData.communityType}
              onChange={(e) => onFieldChange('communityType', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.communityType ? 'border-red-500' : 'border-slate-300'
              }`}
            >
              <option value="">Select community type</option>
              <option value="hoa" title="Single-family homes with shared common areas">
                Homeowners Association (HOA)
              </option>
              <option
                value="condo"
                title="Individual units in buildings with shared building ownership"
              >
                Condominium Association
              </option>
              <option
                value="landowner"
                title="Large lots/parcels, often rural, with fewer shared amenities"
              >
                Land Owner Association
              </option>
              <option value="townhome" title="Attached homes with shared walls and common areas">
                Townhome Community
              </option>
              <option value="gated" title="Secured community with controlled access">
                Gated Community
              </option>
              <option value="mixed" title="Residential and commercial spaces in one development">
                Mixed-Use Development
              </option>
              <option
                value="master"
                title="Large development with multiple neighborhoods and extensive amenities"
              >
                Master-Planned Community
              </option>
              <option value="other">Other</option>
            </select>
            {errors.communityType && (
              <p className="mt-1 text-sm text-red-600">{errors.communityType}</p>
            )}
          </div>
        </div>

        {/* Contact Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <User className="w-5 h-5" />
            Primary Contact Information
          </h3>

          <div>
            <label htmlFor="contactName" className="block text-sm font-medium text-slate-700 mb-1">
              Full Name *
            </label>
            <input
              type="text"
              id="contactName"
              value={formData.contactName}
              onChange={(e) => onFieldChange('contactName', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.contactName ? 'border-red-500' : 'border-slate-300'
              }`}
              placeholder="Enter your full name"
            />
            {errors.contactName && (
              <p className="mt-1 text-sm text-red-600">{errors.contactName}</p>
            )}
          </div>

          <div>
            <label htmlFor="contactTitle" className="block text-sm font-medium text-slate-700 mb-1">
              Your Role/Title in the Community *
            </label>
            <select
              id="contactTitle"
              value={(formData.contactTitle as string) || ''}
              onChange={(e) => onFieldChange('contactTitle', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.contactTitle ? 'border-red-500' : 'border-slate-300'
              }`}
            >
              <option value="">Select your role</option>
              <option value="board-president">Board President</option>
              <option value="board-vice-president">Board Vice President</option>
              <option value="board-secretary">Board Secretary</option>
              <option value="board-treasurer">Board Treasurer</option>
              <option value="board-member">Board Member</option>
              <option value="property-manager">Property Manager</option>
              <option value="community-manager">Community Manager</option>
              <option value="committee-chair">Committee Chair</option>
              <option value="resident">Resident/Homeowner</option>
              <option value="other">Other</option>
            </select>
            {errors.contactTitle && (
              <p className="mt-1 text-sm text-red-600">{errors.contactTitle}</p>
            )}
          </div>

          <div>
            <label htmlFor="contactEmail" className="block text-sm font-medium text-slate-700 mb-1">
              Email Address *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="email"
                id="contactEmail"
                value={formData.contactEmail}
                onChange={(e) => onFieldChange('contactEmail', e.target.value)}
                className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.contactEmail ? 'border-red-500' : 'border-slate-300'
                }`}
                placeholder="your.email@community.com"
              />
            </div>
            {errors.contactEmail && (
              <p className="mt-1 text-sm text-red-600">{errors.contactEmail}</p>
            )}
          </div>

          <div>
            <label htmlFor="contactPhone" className="block text-sm font-medium text-slate-700 mb-1">
              Phone Number
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="tel"
                id="contactPhone"
                value={formData.contactPhone}
                onChange={(e) => onFieldChange('contactPhone', e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="(555) 123-4567"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 rounded-lg p-4 mt-6">
        <p className="text-sm text-blue-800">
          <strong>Privacy Note:</strong> Your information is secure and will only be used to
          personalize your Community App experience. We never share your data with third parties
          without your explicit consent.
        </p>
      </div>
    </div>
  );
};
